import React, { useEffect, useRef } from 'react';

import Image from '@atlaskit/image';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Link from '@atlaskit/primitives/anchor';
import { ff } from '@atlassian/jira-feature-flagging';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { useIsAdmin, useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller';
import AlertsIcon from '../../../common/ui/assets/step-alerts.svg';
import AutomateAlertsAndIncidentsResponseIcon from '../../../common/ui/assets/step-automate-alert-and-incident-response.svg';
import ChatToolsIcon from '../../../common/ui/assets/step-chat-tools.svg';
import DeprecatedOnesIcon from '../../../common/ui/assets/step-deprecated-ones.svg';
import FindIcon from '../../../common/ui/assets/step-find-what-you-are-looking-for.svg';
import HeartbeatsIcon from '../../../common/ui/assets/step-heartbeats.svg';
import IncidentsIcon from '../../../common/ui/assets/step-incidents.svg';
import IntegrationsIcon from '../../../common/ui/assets/step-integrations.svg';
import NotificationsIcon from '../../../common/ui/assets/step-notifications.svg';
import PermissionsIcon from '../../../common/ui/assets/step-permissions.svg';
import TeamsIcon from '../../../common/ui/assets/step-teams-concept.svg';
import { TransitionHomeHeader } from '../../../common/ui/transition-home/header';
import { TransitionHomeStep } from '../../../common/ui/transition-home/step';
import { messages as stepTitleAndDescriptionMessages } from '../../../common/ui/transition-home/step/messages';
import {
	getAlertsTasks,
	getAutomateAlertAndIncidentResponseTasks,
	getChatToolsTasks,
	getDeprecatedOnesTasks,
	getIntegrationsTasks,
	getFindWhatYouAreLookingForTasks,
	getHeartbeatsTasks,
	getIncidentsTasks,
	getNotificationsTasks,
	getTeamsTasks,
	getUsersAndRolesTasks,
} from '../../../common/utils/tasks';
import { useProgressActions, useProgressStore } from '../../../controllers/store/progress';
import { ProgressContents } from '../../../controllers/store/progress/types';
import {
	useChatToolsConnections,
	useChatToolsIntegrations,
} from '../../../services/store/chat-tools';
import {
	useDeprecatedGroups,
	useDeprecatedHeartbeats,
	useDeprecatedNotificationPolicies,
} from '../../../services/store/deprecated-counts';
import { useIncidents } from '../../../services/store/incidents';
import {
	useDeprecatedIntegrations,
	useDiscontinuedIntegrations,
	useIncomingCallIntegrations,
	useIntegrationCounts,
	useSyncedIntegrations,
} from '../../../services/store/integrations';
import { useIsTeamAdmin } from '../../../services/store/team-admin-status';
import { useTeams } from '../../../services/store/teams';
import { useTransitioningStatus } from '../../../services/store/transitioning-status';
import {
	useGetUsersWithoutAdminRole,
	useGetUsersWithoutCustomRole,
	useStakeholders,
} from '../../../services/store/users-and-roles';

import { messages } from './messages';

export const TransitionHomeLeft = () => {
	const { changeActiveContent } = useProgressActions();
	const [{ latestContent }] = useProgressStore();
	const { formatMessage } = useIntl();

	const stepsRef = useRef<{
		[key in ProgressContents]: HTMLElement | null;
	}>({
		[ProgressContents.Home]: null,
		[ProgressContents.UsersAndRoles]: null,
		[ProgressContents.Alerts]: null,
		[ProgressContents.Notifications]: null,
		[ProgressContents.Teams]: null,
		[ProgressContents.ChatTools]: null,
		[ProgressContents.AutomateAlertAndIncidentResponse]: null,
		[ProgressContents.Integrations]: null,
		[ProgressContents.Heartbeats]: null,
		[ProgressContents.Find]: null,
		[ProgressContents.DeprecatedOnes]: null,
		[ProgressContents.Incidents]: null,
	});

	useEffect(() => {
		stepsRef.current[latestContent]?.focus();
	}, [latestContent]);

	const isSiteAdmin = useIsSiteAdmin();
	const isPrimaryAdmin = useIsAdmin();
	const [{ isTeamAdmin }] = useIsTeamAdmin();

	const [usersWithoutAdminRole] = useGetUsersWithoutAdminRole();
	const [usersWithoutCustomRole] = useGetUsersWithoutCustomRole();
	const [stakeholders] = useStakeholders();
	const [connections] = useChatToolsConnections();
	const [{ hasDeprecatedGlobalHeartbeats, hasDeprecatedTotalHeartbeats, isExceedHeartbeatLimit }] =
		useDeprecatedHeartbeats();
	const [{ hasIntegrations }] = useIntegrationCounts();
	const [{ hasTeams }] = useTeams();
	const [{ hasSyncedIntegration }] = useSyncedIntegrations();
	const [{ hasDeprecatedIntegration }] = useDeprecatedIntegrations();
	const [{ hasDiscontinuedIntegration }] = useDiscontinuedIntegrations();
	const [{ hasIncomingCallIntegration }] = useIncomingCallIntegrations();
	const [{ hasDeprecatedGroups }] = useDeprecatedGroups();
	const [{ hasDeprecatedNotificationPolicies }] = useDeprecatedNotificationPolicies();
	const [{ isCopied, shouldDisplaySuccessBanner, isEligibleForCopy }] = useIncidents();
	const [integrations] = useChatToolsIntegrations();

	const [
		{
			isRBNEnabled,
			ogAppExistsAtTransitioningStart,
			areOgAppsDeactivated,
			isJnaAppDownloaded,
			areOgRulesDuplicated,
			error,
			loading,
		},
	] = useTransitioningStatus();

	if (error || loading) {
		// Users should not see this screen if one of the requests is still loading or error
		throw new Error(
			'TransitionHomeLeft should not be rendered if transitioning status is still loading or errored',
		);
	}

	const isOptimalTooling = ff('platform.show-optimal-tooling_brgom');

	return (
		<Box paddingInline="space.400" paddingBlock="space.300" xcss={wrapperBoxStyles}>
			<Stack space="space.250">
				{/* Header */}
				<TransitionHomeHeader
					title={<FormattedMessage {...messages.titleNonFinal} />}
					description={
						<Text>
							<FormattedMessage {...messages.descriptionNonFinal} />
							<Link
								href="https://support.atlassian.com/jira-service-management-cloud/docs/set-up-operations-and-complete-your-shift-to-jira-service-management/"
								target="_blank"
							>
								<FormattedMessage {...messages.getMoreHelpNonFinal} />
							</Link>
						</Text>
					}
				/>
				{/* Users and roles step */}
				{(isSiteAdmin || isPrimaryAdmin) && (
					<TransitionHomeStep
						ref={(element) => {
							stepsRef.current[ProgressContents.UsersAndRoles] = element;
						}}
						onClick={(taskIndex) => changeActiveContent(ProgressContents.UsersAndRoles, taskIndex)}
						icon={
							<Inline
								xcss={[iconBoxStyles, blueBackgroundStyles]}
								alignBlock="center"
								alignInline="center"
							>
								<Image src={PermissionsIcon} alt="" />
							</Inline>
						}
						title={
							<FormattedMessage {...stepTitleAndDescriptionMessages.usersAndRolesTitleNonFinal} />
						}
						description={
							<FormattedMessage
								{...stepTitleAndDescriptionMessages.usersAndRolesDescriptionNonFinal}
							/>
						}
						// content will changeActiveContent when count is 0
						tasks={getUsersAndRolesTasks({
							isOptimalTooling,
							isSiteAdmin,
							adminDifferenceCount: usersWithoutAdminRole?.data?.count || 0,
							customRoleDifferenceCount: usersWithoutCustomRole?.data?.count || 0,
							stakeholderCount: stakeholders?.data?.userCount || 0,
						})}
					/>
				)}
				{/* Alerts step */}
				<TransitionHomeStep
					ref={(element) => {
						stepsRef.current[ProgressContents.Alerts] = element;
					}}
					onClick={(taskIndex) => changeActiveContent(ProgressContents.Alerts, taskIndex)}
					icon={
						<Inline
							xcss={[iconBoxStyles, orangeBackgroundStyles]}
							alignBlock="center"
							alignInline="center"
						>
							<Image src={AlertsIcon} alt="" />
						</Inline>
					}
					title={
						isOptimalTooling
							? formatMessage(stepTitleAndDescriptionMessages.alertsAndOnCallTitleNonFinal)
							: formatMessage(stepTitleAndDescriptionMessages.alertsTitleNonFinal)
					}
					description={
						<FormattedMessage {...stepTitleAndDescriptionMessages.alertsDescriptionNonFinal} />
					}
					tasks={getAlertsTasks(isOptimalTooling)}
				/>
				{/* Notifications step */}
				<TransitionHomeStep
					ref={(element) => {
						stepsRef.current[ProgressContents.Notifications] = element;
					}}
					onClick={(taskIndex) => changeActiveContent(ProgressContents.Notifications, taskIndex)}
					icon={
						<Inline
							xcss={[iconBoxStyles, redSubtlerBackgroundStyles]}
							alignBlock="center"
							alignInline="center"
						>
							<Image src={NotificationsIcon} alt="" />
						</Inline>
					}
					title={
						<FormattedMessage {...stepTitleAndDescriptionMessages.notificationsTitleNonFinal} />
					}
					description={
						<FormattedMessage
							{...stepTitleAndDescriptionMessages.notificationsDescriptionNonFinal}
						/>
					}
					tasks={getNotificationsTasks({
						isRBNEnabled,
						ogAppExistsAtTransitioningStart,
						areOgAppsDeactivated,
						isJnaAppDownloaded,
						areOgRulesDuplicated,
					})}
				/>
				{/* Teams step */}
				<TransitionHomeStep
					ref={(element) => {
						stepsRef.current[ProgressContents.Teams] = element;
					}}
					onClick={(taskIndex) => changeActiveContent(ProgressContents.Teams, taskIndex)}
					icon={
						<Inline
							xcss={[iconBoxStyles, yellowBackgroundStyles]}
							alignBlock="center"
							alignInline="center"
						>
							<Image src={TeamsIcon} alt="" />
						</Inline>
					}
					title={<FormattedMessage {...stepTitleAndDescriptionMessages.teamsTitleNonFinal} />}
					description={
						<FormattedMessage {...stepTitleAndDescriptionMessages.teamsDescriptionNonFinal} />
					}
					tasks={getTeamsTasks(isOptimalTooling, hasTeams)}
				/>
				{/* Chat tools step */}
				{(isPrimaryAdmin || isTeamAdmin) && connections?.data?.count !== 0 && (
					<TransitionHomeStep
						ref={(element) => {
							stepsRef.current[ProgressContents.ChatTools] = element;
						}}
						onClick={(taskIndex) => changeActiveContent(ProgressContents.ChatTools, taskIndex)}
						icon={
							<Inline
								xcss={[iconBoxStyles, blueSublestBackgroundStyles]}
								alignBlock="center"
								alignInline="center"
							>
								<Image src={ChatToolsIcon} alt="" />
							</Inline>
						}
						title={<FormattedMessage {...stepTitleAndDescriptionMessages.chatToolsTitleNonFinal} />}
						description={
							<FormattedMessage {...stepTitleAndDescriptionMessages.chatToolsDescriptionNonFinal} />
						}
						tasks={getChatToolsTasks({
							isChatToolsConnectedToJSM: false,
							isOgIntegrationsCopiedToJSM: false,
							isDuplicateNotificationsPrevented: false,
							isPrimaryAdmin,
							hasIntegration: (integrations?.data?.count || 0) > 0,
						})}
					/>
				)}
				{/* Automate your alert and incident workflows step */}
				{(isPrimaryAdmin || isSiteAdmin || isTeamAdmin) && isOptimalTooling && (
					<TransitionHomeStep
						ref={(element) => {
							stepsRef.current[ProgressContents.AutomateAlertAndIncidentResponse] = element;
						}}
						onClick={(taskIndex) =>
							changeActiveContent(ProgressContents.AutomateAlertAndIncidentResponse, taskIndex)
						}
						icon={
							<Inline
								xcss={[iconBoxStyles, yellowWarningBackgroundStyles]}
								alignBlock="center"
								alignInline="center"
							>
								<Image src={AutomateAlertsAndIncidentsResponseIcon} alt="" />
							</Inline>
						}
						title={formatMessage(
							stepTitleAndDescriptionMessages.automateAlertAndIncidentResponseTitleNonFinal,
						)}
						description={formatMessage(
							stepTitleAndDescriptionMessages.automateAlertAndIncidentResponseDescriptionNonFinal,
						)}
						tasks={getAutomateAlertAndIncidentResponseTasks()}
					/>
				)}

				{/* Integrations step */}
				{(isPrimaryAdmin || isSiteAdmin || isTeamAdmin) && isOptimalTooling && hasIntegrations && (
					<TransitionHomeStep
						ref={(element) => {
							stepsRef.current[ProgressContents.Integrations] = element;
						}}
						onClick={(taskIndex) => changeActiveContent(ProgressContents.Integrations, taskIndex)}
						icon={
							<Inline
								xcss={[iconBoxStyles, opaqSublestBackgroundStyles]}
								alignBlock="center"
								alignInline="center"
							>
								<Image src={IntegrationsIcon} alt="" />
							</Inline>
						}
						title={
							<FormattedMessage {...stepTitleAndDescriptionMessages.integrationsTitleNonFinal} />
						}
						description={
							<FormattedMessage
								{...stepTitleAndDescriptionMessages.integrationsDescriptionNonFinal}
							/>
						}
						tasks={getIntegrationsTasks({
							hasSyncedIntegration,
							hasDiscontinuedIntegration,
							hasDeprecatedIntegration,
							hasIncomingCallIntegration,
							shouldHaveIncomingCallPermission: isSiteAdmin || isPrimaryAdmin,
						})}
					/>
				)}

				{/* Incidents step */}
				{isOptimalTooling && (
					<TransitionHomeStep
						ref={(element) => {
							stepsRef.current[ProgressContents.Incidents] = element;
						}}
						onClick={(taskIndex) => changeActiveContent(ProgressContents.Incidents, taskIndex)}
						icon={
							<Inline
								xcss={[iconBoxStyles, yellowBackgroundStyles]}
								alignBlock="center"
								alignInline="center"
							>
								<Image src={IncidentsIcon} width={40} alt="" />
							</Inline>
						}
						title={formatMessage(stepTitleAndDescriptionMessages.incidentsTitleNonFinal)}
						description={formatMessage(
							stepTitleAndDescriptionMessages.incidentsDescriptionNonFinal,
						)}
						tasks={getIncidentsTasks({
							isCopied,
							shouldDisplaySuccessBanner,
							isEligibleForCopy,
						})}
					/>
				)}

				{/* Heartbeats step */}
				{isOptimalTooling && (hasDeprecatedGlobalHeartbeats || hasDeprecatedTotalHeartbeats) && (
					<TransitionHomeStep
						ref={(element) => {
							stepsRef.current[ProgressContents.Heartbeats] = element;
						}}
						onClick={(taskIndex) => changeActiveContent(ProgressContents.Heartbeats, taskIndex)}
						icon={
							<Inline
								xcss={[iconBoxStyles, blueBackgroundStyles]}
								alignBlock="center"
								alignInline="center"
							>
								<Image src={HeartbeatsIcon} width={40} alt="" />
							</Inline>
						}
						title={formatMessage(stepTitleAndDescriptionMessages.heartbeatsTitleNonFinal)}
						description={formatMessage(
							stepTitleAndDescriptionMessages.heartbeatsDescriptionNonFinal,
						)}
						tasks={getHeartbeatsTasks({
							hasDeprecatedTotalHeartbeats,
							hasDeprecatedGlobalHeartbeats,
							isExceedHeartbeatLimit,
						})}
					/>
				)}

				{/* Find what you're looking for step */}
				{isOptimalTooling && (
					<TransitionHomeStep
						ref={(element) => {
							stepsRef.current[ProgressContents.Find] = element;
						}}
						onClick={(taskIndex) => changeActiveContent(ProgressContents.Find, taskIndex)}
						icon={
							<Inline
								xcss={[iconBoxStyles, yellowBackgroundStyles]}
								alignBlock="center"
								alignInline="center"
							>
								<Image src={FindIcon} width={40} alt="" />
							</Inline>
						}
						title={formatMessage(
							stepTitleAndDescriptionMessages.findWhatYouAreLookingForTitleNonFinal,
						)}
						description={formatMessage(
							stepTitleAndDescriptionMessages.findWhatYouAreLookingForDescriptionNonFinal,
						)}
						tasks={getFindWhatYouAreLookingForTasks()}
					/>
				)}

				{/* Deprecated ones step */}
				{(isPrimaryAdmin || isSiteAdmin || isTeamAdmin) && isOptimalTooling && (
					<TransitionHomeStep
						ref={(element) => {
							stepsRef.current[ProgressContents.DeprecatedOnes] = element;
						}}
						onClick={(taskIndex) => changeActiveContent(ProgressContents.DeprecatedOnes, taskIndex)}
						icon={
							<Inline
								xcss={[iconBoxStyles, redSubtlestBackgroundStyles]}
								alignBlock="center"
								alignInline="center"
							>
								<Image src={DeprecatedOnesIcon} alt="" />
							</Inline>
						}
						title={
							<FormattedMessage {...stepTitleAndDescriptionMessages.deprecatedOnesTitleNonFinal} />
						}
						description={
							<FormattedMessage
								{...stepTitleAndDescriptionMessages.deprecatedOnesDescriptionNonFinal}
							/>
						}
						tasks={getDeprecatedOnesTasks({
							isAdmin: isSiteAdmin || isPrimaryAdmin,
							hasDeprecatedIntegration,
							hasDeprecatedGroups,
							hasDeprecatedGlobalHeartbeats,
							hasDeprecatedNotificationPolicies,
						})}
					/>
				)}
			</Stack>
		</Box>
	);
};

const iconBoxStyles = xcss({
	flexShrink: 0,
	borderRadius: 'border.radius.200',
	width: '56px',
	height: '56px',
});
const orangeBackgroundStyles = xcss({ backgroundColor: 'color.background.accent.orange.subtler' });
const blueBackgroundStyles = xcss({ backgroundColor: 'color.background.accent.blue.subtler' });
const redSubtlerBackgroundStyles = xcss({ backgroundColor: 'color.background.accent.red.subtler' });
const redSubtlestBackgroundStyles = xcss({
	backgroundColor: 'color.background.accent.red.subtlest',
});
const yellowBackgroundStyles = xcss({ backgroundColor: 'color.background.accent.yellow.subtler' });
const yellowWarningBackgroundStyles = xcss({ backgroundColor: 'color.background.warning' });
const blueSublestBackgroundStyles = xcss({
	backgroundColor: 'color.background.accent.blue.subtlest',
});
const opaqSublestBackgroundStyles = xcss({
	backgroundColor: 'color.background.accent.gray.subtler',
});

const wrapperBoxStyles = xcss({
	height: '100%',
	overflow: 'auto',
});
